import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import { useLanguage } from '../../contexts/LanguageContext';

const CSS = `
  .im-card{background:#FAFAFA;border-radius:10px;padding:12px;border:1px solid #EDEDED;transition:background .15s,border-color .15s;}
  .im-card:hover{background:#fff;border-color:#D4D4D4;}
  .im-badge{display:inline-flex;align-items:center;font-size:11px;font-weight:500;padding:2px 8px;border-radius:980px;letter-spacing:-.01em;font-family:'Inter',system-ui,sans-serif;}
  .im-action{background:#F5F5F5;border-radius:8px;padding:8px 10px;border:1px solid #EBEBEB;margin-top:8px;}
  .im-pred{background:#fff;border-radius:10px;padding:10px 12px;border:1px solid #E5E5E5;transition:border-color .15s;}
  .im-pred:hover{border-color:rgba(10,10,10,0.25);}
  .im-spin{width:24px;height:24px;border:2px solid rgba(0,0,0,0.08);border-top-color:#0A0A0A;border-radius:50%;animation:im-rotate .8s linear infinite;}
  @keyframes im-rotate{to{transform:rotate(360deg)}}
`;

const PRIORITY = {
  high:   { label: 'Alta',  bg: 'rgba(220,38,38,0.1)',  color: '#DC2626' },
  medium: { label: 'Media', bg: 'rgba(217,119,6,0.1)',  color: '#D97706' },
  low:    { label: 'Baja',  bg: 'rgba(107,107,107,0.1)', color: '#6B6B6B' },
};
const priorityLabel = (key, t) => {
  const resolvedKey = PRIORITY[key] ? key : 'low';
  return t(`insights.priority.${resolvedKey}`, PRIORITY[resolvedKey].label);
};

const ICON_MAP = {
  'trending-up': 'trending', star: 'star', target: 'star',
  network: 'globe', rocket: 'trending', alert: 'warning',
  calendar: 'calendar', play: 'play',
};

// Trend arrow — SVG inline, no emojis
const TrendIcon = ({ trend }) => {
  const cfg = {
    up:       { d: 'M12 19V5M5 12l7-7 7 7', color: '#16A34A' },
    down:     { d: 'M12 5v14M5 12l7 7 7-7', color: '#DC2626' },
    stable:   { d: 'M5 12h14M12 5l7 7-7 7', color: '#6B6B6B' },
    positive: { d: 'M20 6L9 17l-5-5',       color: '#16A34A' },
    negative: { d: 'M12 9v4m0 4h.01',        color: '#D97706' },
  }[trend] || { d: 'M12 9v4m0 4h.01', color: '#9A9A9A' };

  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={cfg.d}/>
    </svg>
  );
};

const InsightsModule = ({ profile, dateRange }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-ES';
  const [insights, setInsights]       = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { if (profile) loadInsights(); }, [profile, dateRange]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/analytics/${profile.id}/insights`, {
        params:  { date_range: dateRange },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setInsights(data.insights    || []);
      setPredictions(data.predictions || []);
      setLastUpdated(data.generated_at);
    } catch {
      setInsights([]);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-card" style={{ padding: '20px 20px', height: '100%' }}>
      <style>{CSS}</style>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: '#F5F5F5',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name="brain" className="w-4 h-4" style={{ color: '#0A0A0A' }} />
        </div>
        <div>
          <h3 style={{
            fontSize: 15, fontWeight: 600, color: '#1d1d1f',
            letterSpacing: '-0.02em', margin: 0,
            fontFamily: "'Inter', system-ui, sans-serif",
          }}>
            {t('insights.title', 'Insights IA')}
          </h3>
        </div>
      </div>

      {/* ── Loading ─────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '28px 0' }}>
          <div className="im-spin" style={{ margin: '0 auto' }} />
          <p style={{
            marginTop: 10, fontSize: 12, color: '#6e6e73',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}>
            {t('insights.analyzing', 'Analizando datos…')}
          </p>
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────── */}
      {!loading && insights.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Last updated */}
          {lastUpdated && (
            <p style={{
              fontSize: 11, color: '#aeaeb2', textAlign: 'center',
              paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.06)',
              fontFamily: "'Inter', system-ui, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              {t('insights.updated', 'Actualizado')} {new Date(lastUpdated).toLocaleString(dateLocale, {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          )}

          {/* Insight cards */}
          {insights.slice(0, 3).map((insight, i) => {
            const pri = PRIORITY[insight.priority] || PRIORITY.low;
            return (
              <div key={i} className="im-card">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, flex: 1 }}>
                    <Icon
                      name={ICON_MAP[insight.icon] || 'brain'}
                      className="w-4 h-4"
                      style={{ color: '#0A0A0A', flexShrink: 0, marginTop: 1 }}
                    />
                    <span style={{
                      fontSize: 13, fontWeight: 600, color: '#1d1d1f',
                      letterSpacing: '-0.015em', lineHeight: 1.3,
                      fontFamily: "'Inter', system-ui, sans-serif",
                    }}>
                      {insight.title}
                    </span>
                  </div>
                  <span className="im-badge" style={{ background: pri.bg, color: pri.color, flexShrink: 0 }}>
                    {priorityLabel(insight.priority, t)}
                  </span>
                </div>

                <p style={{
                  fontSize: 12, color: '#6e6e73', lineHeight: 1.55, margin: 0,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  letterSpacing: '-0.01em',
                }}>
                  {insight.description}
                </p>

                {insight.action && (
                  <div className="im-action">
                    <p style={{
                      fontSize: 12, fontWeight: 500, color: '#1d1d1f', margin: 0,
                      fontFamily: "'Inter', system-ui, sans-serif",
                      letterSpacing: '-0.01em',
                    }}>
                      {insight.action}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────── */}
      {!loading && insights.length === 0 && (
        <div style={{ textAlign: 'center', padding: '28px 0' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <Icon name="brain" className="w-6 h-6" style={{ color: '#aeaeb2' }} />
          </div>
          <p style={{
            fontSize: 13, fontWeight: 500, color: '#1d1d1f', margin: '0 0 4px',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}>
            {t('insights.empty.title', 'Sin insights disponibles')}
          </p>
          <p style={{
            fontSize: 12, color: '#aeaeb2', margin: 0,
            fontFamily: "'Inter', system-ui, sans-serif",
          }}>
            {t('insights.empty.sub', 'Necesitas más actividad para generar recomendaciones')}
          </p>
        </div>
      )}
    </div>
  );
};

export default InsightsModule;
